// src/components/SupportModule.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    FaHeadset, FaPlus, FaArrowLeft, FaPaperclip, FaSpinner, FaPaperPlane,
    FaUserShield, FaDownload, FaInfoCircle, FaHashtag,
    FaRegTired, FaRegFrown, FaRegMeh, FaRegSmile, FaRegGrinBeam, FaStar,
    FaCheckCircle, FaLock, FaClock, FaExclamationCircle, FaInbox,
    FaChevronRight, FaTimes,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import api from '../services/api';
import { useSocket } from '../components/SocketContext';
import './SupportModule.css';

// Escopos que indicam categoria gerida pela administração do sistema (admin_geral)
const ESCOPOS_GLOBAIS = ['GLOBAL_TODOS', 'SEGMENTADO'];


// ─── NewTicketForm ────────────────────────────────────────────────────────
const NewTicketForm = ({ user, onTicketCreated, onCancel, onClose }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        assunto: '', mensagem: '', prioridade: 'Normal', tipo: 'Incidente',
        categoria_id: '', subcategoria_id: '', perfil_destino: ''
    });
    const [opcoesPrioridade,       setOpcoesPrioridade]       = useState([]);
    const [categorias,             setCategorias]             = useState([]);
    const [subcategoriasFiltradas, setSubcategoriasFiltradas] = useState([]);
    const [loadingCategories,      setLoadingCategories]      = useState(true);
    const [attachment,             setAttachment]             = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        api.get('/api/suporte/categorias-ativas')
            .then(({ data }) => setCategorias(data))
            .catch(() => toast.error('Não foi possível carregar as categorias.'))
            .finally(() => setLoadingCategories(false));
    }, []);

    // Categoria atualmente selecionada + flag de "global" (gerida pelo admin do sistema)
    const categoriaSelecionada = useMemo(
        () => categorias.find(c => c.id === formData.categoria_id) || null,
        [categorias, formData.categoria_id]
    );
    const isCategoriaGlobal = !!categoriaSelecionada
        && ESCOPOS_GLOBAIS.includes(categoriaSelecionada.escopo);

    useEffect(() => {
        const opts = formData.tipo === 'Incidente'
            ? [
                { value: 'Normal',  label: 'Normal — Atrapalha meu trabalho'     },
                { value: 'Alta',    label: 'Alta — Estou parado por causa disso' },
                { value: 'Urgente', label: 'Urgente — Impacto crítico no sistema'},
              ]
            : [
                { value: 'Baixa',  label: 'Baixa — Posso continuar trabalhando' },
                { value: 'Normal', label: 'Normal — Atrapalha meu trabalho'     },
              ];
        setOpcoesPrioridade(opts);
        if (!opts.some(p => p.value === formData.prioridade)) {
            setFormData(prev => ({ ...prev, prioridade: opts[0].value }));
        }
    }, [formData.tipo, formData.prioridade]);

    const handleCategoryChange = (catId) => {
        const id  = parseInt(catId, 10);
        const cat = categorias.find(c => c.id === id);
        setSubcategoriasFiltradas(cat?.subcategorias || []);
        setFormData(prev => ({
            ...prev,
            categoria_id:    cat ? id : '',
            subcategoria_id: '',
            tipo:            cat?.tipo_padrao            || 'Incidente',
            prioridade:      cat?.prioridade_padrao      || 'Normal',
            perfil_destino:  cat?.perfil_destino_padrao  || '',
        }));
    };

    const handleFileChange = (e) => {
        const f = e.target.files[0];
        if (f && f.size > 5 * 1024 * 1024) { toast.error('Máx 5 MB.'); e.target.value = null; return; }
        setAttachment(f);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.assunto || !formData.mensagem || !formData.categoria_id || !formData.subcategoria_id)
            return toast.warn('Preencha todos os campos obrigatórios.');
        setIsSubmitting(true);
        const fd = new FormData();
        Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
        fd.append('logs_console', localStorage.getItem('consoleLogs') || '');
        fd.append('logs_rede', JSON.stringify(window.performance.getEntriesByType('resource').slice(-50)));
        if (attachment) fd.append('anexo', attachment);
        try {
            const { data } = await api.post('/api/suporte/chamados', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(
                isCategoriaGlobal
                    ? 'Chamado enviado para o administrador do sistema!'
                    : 'Chamado aberto com sucesso!'
            );
            onTicketCreated(data.id);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Erro ao enviar chamado.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="support-view">
            {/* Header com botão voltar + título + fechar */}
            <div className="sm-modal-header">
                <button onClick={onCancel} className="sm-back-btn" title="Voltar">
                    <FaArrowLeft />
                </button>
                <div className="sm-header-title">
                    <span className="sm-header-icon">🎫</span>
                    <h1 className="sm-page-title">Abrir Chamado</h1>
                </div>
                <button onClick={onClose} className="sm-close-btn" title="Fechar suporte">
                    <FaTimes />
                </button>
            </div>

            <p className="sm-view-subtitle">
                Descreva seu problema com detalhes. Logs técnicos serão anexados automaticamente.
            </p>

            <form onSubmit={handleSubmit} className="support-form">
                <div className="form-row">
                    <div className="form-group">
                        <label>Tipo de Solicitação *</label>
                        <select name="tipo" value={formData.tipo} onChange={e => setFormData(p => ({ ...p, tipo: e.target.value }))}>
                            <option value="Incidente">🔴 Incidente (algo quebrou)</option>
                            <option value="Requisição">🔵 Requisição (preciso de algo)</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Urgência *</label>
                        <select name="prioridade" value={formData.prioridade} onChange={e => setFormData(p => ({ ...p, prioridade: e.target.value }))}>
                            {opcoesPrioridade.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label>Categoria *</label>
                        <select value={formData.categoria_id} onChange={e => handleCategoryChange(e.target.value)} required disabled={loadingCategories}>
                            <option value="">{loadingCategories ? 'Carregando…' : 'Selecione'}</option>
                            {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Subcategoria *</label>
                        <select name="subcategoria_id" value={formData.subcategoria_id} onChange={e => setFormData(p => ({ ...p, subcategoria_id: e.target.value }))} required disabled={!formData.categoria_id}>
                            <option value="">Selecione</option>
                            {subcategoriasFiltradas.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                        </select>
                    </div>
                </div>

                {/* Aviso: categoria gerida pela administração do sistema */}
                {isCategoriaGlobal && (
                    <div className="sm-global-notice" role="status">
                        <div className="sm-global-notice-icon"><FaUserShield /></div>
                        <div className="sm-global-notice-text">
                            <strong>Atendimento pela administração do sistema</strong>
                            <span>
                                Esta é uma categoria global. Ao enviar, seu chamado será encaminhado
                                diretamente ao <b>administrador do sistema</b> — e não ao suporte da sua empresa.
                            </span>
                        </div>
                    </div>
                )}

                <div className="form-group">
                    <label>Assunto *</label>
                    <input type="text" name="assunto" value={formData.assunto} onChange={e => setFormData(p => ({ ...p, assunto: e.target.value }))} placeholder="Ex: Erro ao finalizar OS" required />
                </div>
                <div className="form-group">
                    <label>Descrição detalhada *</label>
                    <textarea name="mensagem" rows="5" value={formData.mensagem} onChange={e => setFormData(p => ({ ...p, mensagem: e.target.value }))} placeholder="Descreva os passos que levaram ao erro…" required />
                </div>
                <div className="form-group attachment-group">
                    <label>Anexo (opcional, máx 5 MB)</label>
                    <button type="button" onClick={() => fileInputRef.current.click()} className="btn-attach">
                        <FaPaperclip /> {attachment ? attachment.name : 'Escolher arquivo'}
                    </button>
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
                </div>
                <button type="submit" className="btn-send-support" disabled={isSubmitting}>
                    {isSubmitting ? <FaSpinner className="spinner" /> : <FaPaperPlane />}
                    {isSubmitting
                        ? ' Enviando…'
                        : isCategoriaGlobal ? ' Enviar à Administração' : ' Enviar Chamado'}
                </button>
            </form>
        </div>
    );
};


// ─── Avaliação ────────────────────────────────────────────────────────────
const ratingOptions = [
    { value: 1, icon: FaRegTired,    label: 'Péssimo'   },
    { value: 2, icon: FaRegFrown,    label: 'Ruim'      },
    { value: 3, icon: FaRegMeh,      label: 'Normal'    },
    { value: 4, icon: FaRegSmile,    label: 'Bom'       },
    { value: 5, icon: FaRegGrinBeam, label: 'Excelente' },
];

const SatisfactionSurveyModal = ({ isOpen, onClose, onSubmit, ticketId }) => {
    const [perguntas,    setPerguntas]    = useState([]);
    const [respostas,    setRespostas]    = useState({});
    const [loading,      setLoading]      = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        api.get('/api/suporte/satisfacao/perguntas')
            .then(({ data }) => {
                setPerguntas(data.sort((a, b) => a.ordem - b.ordem));
                const init = {};
                data.forEach(p => { init[p.id] = p.tipo === 'nota_5' ? 0 : ''; });
                setRespostas(init);
            })
            .catch(() => { toast.error('Erro ao carregar pesquisa.'); onClose(); })
            .finally(() => setLoading(false));
    }, [isOpen, onClose]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        for (const p of perguntas) {
            if (!p.obrigatoria) continue;
            const r = respostas[p.id];
            if (p.tipo === 'nota_5' && (!r || r === 0))   { toast.warn(`"${p.texto}" é obrigatória.`); return; }
            if (p.tipo !== 'nota_5' && (!r || !r.trim())) { toast.warn(`"${p.texto}" é obrigatória.`); return; }
        }
        setIsSubmitting(true);
        await onSubmit(Object.keys(respostas).map(id => ({
            pergunta_id: parseInt(id),
            resposta:    respostas[id]?.toString() || null,
        })).filter(r => r.resposta));
        setIsSubmitting(false);
    };

    const PerguntaNota5 = ({ value, onChange }) => {
        const [hover, setHover] = useState(0);
        return (
            <div className="satisfaction-rating">
                <div className="rating-options">
                    {ratingOptions.map(opt => {
                        const Icon = opt.icon;
                        const color = hover >= opt.value ? '#ffc107' : value >= opt.value ? '#28a745' : '#adb5bd';
                        return (
                            <div key={opt.value} className="rating-option"
                                onClick={() => onChange(opt.value)}
                                onMouseEnter={() => setHover(opt.value)}
                                onMouseLeave={() => setHover(0)}
                            >
                                <Icon size={40} style={{ color }} />
                                <span className="rating-label" style={{ color: value >= opt.value ? '#28a745' : '#6c757d' }}>
                                    {opt.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="survey-modal-overlay" onClick={onClose}>
            <div className="survey-modal-content" onClick={e => e.stopPropagation()}>
                <button className="sm-close-btn survey-close" onClick={onClose}><FaTimes /></button>
                {loading ? (
                    <div className="loading-view"><FaSpinner className="spinner" /> Carregando…</div>
                ) : (
                    <>
                        <h3>Como foi sua experiência?</h3>
                        <p>Seu feedback melhora nosso atendimento no chamado #{ticketId}.</p>
                        <form onSubmit={handleSubmit} className="dynamic-survey-form">
                            {perguntas.map(p => (
                                <div className="form-group" key={p.id}>
                                    <label>{p.texto}{p.obrigatoria && <span className="obrigatorio">*</span>}</label>
                                    {p.tipo === 'nota_5'
                                        ? <PerguntaNota5 value={respostas[p.id] || 0} onChange={v => setRespostas(prev => ({ ...prev, [p.id]: v }))} />
                                        : p.tipo === 'texto_aberto'
                                        ? <textarea rows="4" value={respostas[p.id] || ''} onChange={e => setRespostas(prev => ({ ...prev, [p.id]: e.target.value }))} placeholder="Digite sua resposta…" />
                                        : <select className="support-form-select" value={respostas[p.id] || ''} onChange={e => setRespostas(prev => ({ ...prev, [p.id]: e.target.value }))}>
                                            <option value="">Selecione…</option>
                                            {p.opcoes.map(o => <option key={o} value={o}>{o}</option>)}
                                          </select>
                                    }
                                </div>
                            ))}
                            <button type="submit" className="btn-submit-survey" disabled={isSubmitting}>
                                {isSubmitting ? <FaSpinner className="spinner" /> : 'Enviar Avaliação'}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

const TicketRatingDisplay = ({ rating, comment }) => (
    <div className="ticket-rating-display">
        <h4>Sua Avaliação</h4>
        <div className="rating-stars">
            {[...Array(5)].map((_, i) => <FaStar key={i} color={i < rating ? '#ffc107' : '#e4e5e9'} />)}
            <span className="rating-value">({rating}/5)</span>
        </div>
        {comment && (
            <div className="rating-comment-display">
                <strong>Comentário:</strong>
                <p>"{comment}"</p>
            </div>
        )}
    </div>
);


// ─── Status config ────────────────────────────────────────────────────────
const STATUS_CONFIG = {
    'Aberto':             { color: '#3b82f6', bg: '#eff6ff', label: 'Aberto',            icon: FaInfoCircle       },
    'Em Análise':         { color: '#8b5cf6', bg: '#f5f3ff', label: 'Em Análise',         icon: FaInfoCircle       },
    'Aguardando Suporte': { color: '#06b6d4', bg: '#ecfeff', label: 'Aguard. Suporte',    icon: FaClock            },
    'Aguardando Cliente': { color: '#f59e0b', bg: '#fffbeb', label: 'Resp. Necessária',   icon: FaExclamationCircle},
    'Resolvido':          { color: '#10b981', bg: '#f0fdf4', label: 'Resolvido',          icon: FaCheckCircle      },
    'Fechado':            { color: '#94a3b8', bg: '#f8fafc', label: 'Fechado',            icon: FaLock             },
};
const getStatusConfig = s => STATUS_CONFIG[s] ?? { color: '#94a3b8', bg: '#f8fafc', label: s, icon: FaInfoCircle };


// ─── TicketListView ───────────────────────────────────────────────────────
const TicketListView = ({ onSelectTicket, onNewTicket, onGroupUpdate, onClose }) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const socket = useSocket();

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get('/api/suporte/chamados'); setTickets(data); }
        catch { toast.error('Não foi possível carregar seus chamados.'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    useEffect(() => {
        if (!socket) return;
        const onStatus = ({ chamadoId, newStatus }) =>
            setTickets(prev => prev.map(t => String(t.id) === String(chamadoId) ? { ...t, status: newStatus, atualizado_em: new Date().toISOString() } : t));
        socket.on('support_ticket_status_changed', onStatus);
        socket.on('support_notification', fetchTickets);
        return () => { socket.off('support_ticket_status_changed', onStatus); socket.off('support_notification', fetchTickets); };
    }, [socket, fetchTickets]);

    const grouped = useMemo(() => {
        const g = { aguardando: [], emAndamento: [], fechados: [] };
        for (const t of tickets) {
            if (t.status === 'Aguardando Cliente')                                   g.aguardando.push(t);
            else if (['Aberto','Em Análise','Aguardando Suporte'].includes(t.status)) g.emAndamento.push(t);
            else                                                                      g.fechados.push(t);
        }
        return g;
    }, [tickets]);

    useEffect(() => { onGroupUpdate?.(grouped.aguardando.length); }, [grouped.aguardando.length, onGroupUpdate]);

    const renderCard = (ticket) => {
        const cfg = getStatusConfig(ticket.status);
        const Icon = cfg.icon;
        const isAguardando = ticket.status === 'Aguardando Cliente';
        const timeAgo = ticket.atualizado_em
            ? formatDistanceToNow(parseISO(ticket.atualizado_em), { locale: ptBR, addSuffix: true })
            : '—';
        return (
            <li key={ticket.id} className={`sm-ticket-card ${isAguardando ? 'awaiting' : ''}`}
                style={{ '--card-color': cfg.color }} onClick={() => onSelectTicket(ticket.id)}>
                <div className="sm-card-strip" />
                <div className="sm-card-body">
                    <div className="sm-card-top">
                        <span className="sm-ticket-id"><FaHashtag /> {ticket.id}</span>
                        <span className="sm-status-pill" style={{ background: cfg.bg, color: cfg.color }}>
                            <Icon style={{ fontSize: '0.65rem' }} /> {cfg.label}
                        </span>
                    </div>
                    <strong className="sm-ticket-subject">{ticket.assunto}</strong>
                    <div className="sm-card-footer">
                        <span className="sm-time">{timeAgo}</span>
                        <FaChevronRight className="sm-arrow" />
                    </div>
                </div>
            </li>
        );
    };

    const GroupHeader = ({ label, count, variant }) => (
        <div className={`sm-group-header ${variant}`}>
            <span className="sm-group-label">{label}</span>
            <span className="sm-group-count">{count}</span>
        </div>
    );

    return (
        <div className="support-view">
            {/* Header: título + Abrir Chamado + X */}
            <div className="sm-modal-header">
                <div className="sm-header-title">
                    <span className="sm-header-icon">🎧</span>
                    <h1 className="sm-page-title">Meus Chamados</h1>
                </div>
                <div className="sm-header-actions">
                    <button onClick={onNewTicket} className="btn-new-ticket">
                        <FaPlus /> <span className="btn-label">Abrir Chamado</span>
                    </button>
                    <button onClick={onClose} className="sm-close-btn" title="Fechar">
                        <FaTimes />
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="loading-view"><FaSpinner className="spinner" /><span>Carregando…</span></div>
            ) : tickets.length === 0 ? (
                <div className="sm-empty-state">
                    <div className="sm-empty-icon"><FaInbox /></div>
                    <h3>Tudo tranquilo por aqui!</h3>
                    <p>Nenhum chamado aberto. Precisa de ajuda?</p>
                    <button className="btn-new-ticket" onClick={onNewTicket}><FaPlus /> Abrir Chamado</button>
                </div>
            ) : (
                <ul className="sm-ticket-list">
                    {grouped.aguardando.length > 0 && (<><GroupHeader label="Precisa da sua resposta" count={grouped.aguardando.length} variant="awaiting" />{grouped.aguardando.map(renderCard)}</>)}
                    {grouped.emAndamento.length > 0 && (<><GroupHeader label="Em Andamento" count={grouped.emAndamento.length} variant="active" />{grouped.emAndamento.map(renderCard)}</>)}
                    {grouped.fechados.length > 0    && (<><GroupHeader label="Encerrados"   count={grouped.fechados.length}    variant="closed" />{grouped.fechados.map(renderCard)}</>)}
                </ul>
            )}
        </div>
    );
};


// ─── TicketDetailView ─────────────────────────────────────────────────────
const TicketDetailView = ({ ticketId, onBack, onClose }) => {
    const [ticket,            setTicket]            = useState(null);
    const [loading,           setLoading]           = useState(true);
    const [newMessage,        setNewMessage]        = useState('');
    const [attachment,        setAttachment]        = useState(null);
    const [isSending,         setIsSending]         = useState(false);
    const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);

    const timelineEndRef = useRef(null);
    const fileInputRef   = useRef(null);
    const socket         = useSocket();

    const user = useMemo(() => { try { return JSON.parse(localStorage.getItem('user')) || {}; } catch { return {}; } }, []);

    const fetchTicket = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get(`/api/suporte/chamados/${ticketId}`); setTicket(data); }
        catch { toast.error('Não foi possível carregar o chamado.'); }
        finally { setLoading(false); }
    }, [ticketId]);

    useEffect(() => { fetchTicket(); }, [fetchTicket]);
    useEffect(() => {
        if (ticket?.status === 'Resolvido' && !ticket.avaliacao_nota) {
            const t = setTimeout(() => setIsSurveyModalOpen(true), 1000);
            return () => clearTimeout(t);
        }
    }, [ticket]);

    useEffect(() => {
        if (!socket || !ticketId) return;
        socket.emit('join_support_ticket', ticketId);
        const onMsg = msg => {
            if (String(msg.chamado_id) === String(ticketId))
                setTicket(prev => prev ? ({ ...prev, mensagens: [...(prev.mensagens || []), msg] }) : prev);
        };
        const onStatus = ({ chamadoId, newStatus }) => {
            if (String(chamadoId) !== String(ticketId)) return;
            toast.info(`Status: ${newStatus}`);
            newStatus === 'Resolvido' ? fetchTicket() : setTicket(prev => prev ? { ...prev, status: newStatus } : prev);
        };
        socket.on('new_support_message', onMsg);
        socket.on('support_ticket_status_changed', onStatus);
        return () => {
            socket.emit('leave_support_ticket', ticketId);
            socket.off('new_support_message', onMsg);
            socket.off('support_ticket_status_changed', onStatus);
        };
    }, [socket, ticketId, fetchTicket]);

    useEffect(() => { timelineEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [ticket?.mensagens]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() && !attachment) return;
        setIsSending(true);
        const fd = new FormData();
        fd.append('texto', newMessage);
        if (attachment) fd.append('anexo', attachment);
        try {
            await api.post(`/api/suporte/chamados/${ticketId}/mensagens`, fd);
            setNewMessage(''); setAttachment(null);
            if (fileInputRef.current) fileInputRef.current.value = null;
        } catch (err) {
            toast.error(err.response?.data?.error || 'Falha ao enviar mensagem.');
        } finally { setIsSending(false); }
    };

    const handleFileChange = (e) => {
        const f = e.target.files[0];
        if (f && f.size > 5 * 1024 * 1024) { toast.error('Máx 5 MB.'); e.target.value = null; return; }
        setAttachment(f);
    };

    const handleSurveySubmit = async (payload) => {
        try {
            await api.post(`/api/suporte/chamados/${ticketId}/avaliar`, { respostas: payload });
            toast.success('Obrigado pelo feedback!');
            setIsSurveyModalOpen(false);
            fetchTicket();
        } catch { toast.error('Erro ao enviar avaliação.'); }
    };

    if (loading) return <div className="loading-view"><FaSpinner className="spinner" /><span>Carregando…</span></div>;
    if (!ticket) return <div className="loading-view">Chamado não encontrado.</div>;

    const isClosed  = ['Resolvido', 'Fechado'].includes(ticket.status);
    const statusCfg = getStatusConfig(ticket.status);
    const StatusIcon = statusCfg.icon;

    return (
        <div className="support-view">
            {/* Header: voltar + info + status + X */}
            <div className="sm-modal-header">
                <button onClick={onBack} className="sm-back-btn" title="Voltar"><FaArrowLeft /></button>
                <div className="sm-detail-header-info">
                    <span className="sm-detail-id">#{ticket.id}</span>
                    <h1 className="sm-detail-subject">{ticket.assunto}</h1>
                </div>
                <div className="sm-header-actions">
                    <span className="sm-status-pill large" style={{ background: statusCfg.bg, color: statusCfg.color }}>
                        <StatusIcon /> {statusCfg.label}
                    </span>
                    <button onClick={onClose} className="sm-close-btn" title="Fechar"><FaTimes /></button>
                </div>
            </div>

            {ticket.avaliacao_nota > 0 && <TicketRatingDisplay rating={ticket.avaliacao_nota} comment={ticket.avaliacao_comentario} />}

            <div className="sm-chat-area">
                {(ticket.mensagens || []).map(msg => {
                    const isMe     = Number(msg.remetente_id) === Number(user.id);
                    const isSystem = msg.remetente_tipo === 'system';
                    if (isSystem) return <div key={msg.id} className="sm-system-event"><span>{msg.texto}</span></div>;
                    return (
                        <div key={msg.id} className={`sm-message-row ${isMe ? 'me' : 'support'}`}>
                            {!isMe && <div className="sm-avatar"><FaUserShield /></div>}
                            <div className="sm-bubble">
                                {!isMe && <span className="sm-bubble-name">{msg.remetente_nome}</span>}
                                <p>{msg.texto}</p>
                                {msg.tipo_mensagem === 'anexo' && msg.anexo_url && (
                                    <a href={msg.anexo_url} target="_blank" rel="noopener noreferrer" className="sm-attachment">
                                        <FaDownload /> {msg.anexo_nome || 'Baixar anexo'}
                                    </a>
                                )}
                                <span className="sm-bubble-time">
                                    {msg.criado_em ? format(parseISO(msg.criado_em), "dd/MM 'às' HH:mm", { locale: ptBR }) : ''}
                                </span>
                            </div>
                        </div>
                    );
                })}
                <div ref={timelineEndRef} />
            </div>

            {isClosed && (
                <div className="sm-closed-banner">
                    <FaLock />
                    <span><strong>Chamado {ticket.status}.</strong> Não é possível enviar novas mensagens.</span>
                </div>
            )}

            {!isClosed && (
                <form onSubmit={handleSendMessage} className="sm-message-form">
                    {attachment && (
                        <div className="sm-attach-preview">
                            <FaPaperclip /> {attachment.name}
                            <button type="button" onClick={() => { setAttachment(null); if (fileInputRef.current) fileInputRef.current.value = null; }}>×</button>
                        </div>
                    )}
                    <div className="sm-input-row">
                        <button type="button" className="sm-attach-btn" onClick={() => fileInputRef.current?.click()} title="Anexar">
                            <FaPaperclip />
                        </button>
                        <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
                        <input type="text" className="sm-text-input" value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Digite sua mensagem…" disabled={isSending} />
                        <button type="submit" className="sm-send-btn" disabled={isSending || (!newMessage.trim() && !attachment)}>
                            {isSending ? <FaSpinner className="spinner" /> : <FaPaperPlane />}
                        </button>
                    </div>
                </form>
            )}

            <SatisfactionSurveyModal isOpen={isSurveyModalOpen} onClose={() => setIsSurveyModalOpen(false)} onSubmit={handleSurveySubmit} ticketId={ticket.id} />
        </div>
    );
};


// ─── SupportModule (raiz) ─────────────────────────────────────────────────
export default function SupportModule({ user }) {
    const [isOpen,           setIsOpen]           = useState(false);
    const [currentView,      setCurrentView]      = useState('list');
    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [hasNotification,  setHasNotification]  = useState(false);
    const [awaitingCount,    setAwaitingCount]    = useState(0);
    const socket = useSocket();

    useEffect(() => {
        if (!socket) return;
        const handle = () => { if (!isOpen) setHasNotification(true); };
        socket.on('support_notification', handle);
        return () => socket.off('support_notification', handle);
    }, [socket, isOpen]);

    const handleOpenModal  = () => { setIsOpen(true); setHasNotification(false); };
    const handleCloseModal = () => { setIsOpen(false); setTimeout(() => { setSelectedTicketId(null); setCurrentView('list'); }, 300); };
    const needsAttention   = (awaitingCount > 0 || hasNotification) && !isOpen;

    return (
        <>
            {/* FAB */}
            <button
                className={`support-fab ${needsAttention ? 'needs-attention' : ''} ${hasNotification ? 'has-notification' : ''}`}
                onClick={handleOpenModal}
                title="Suporte Técnico"
                aria-label="Abrir suporte"
            >
                <FaHeadset size={26} />
                {awaitingCount > 0 && !isOpen && (
                    <span className="fab-awaiting-badge">{awaitingCount}</span>
                )}
                {hasNotification && <span className="notification-dot" />}
            </button>

            {/* Modal — SEM modal-close-button flutuante */}
            {isOpen && (
                <div className="sm-overlay" onClick={handleCloseModal}>
                    <div className="support-modal-content" onClick={e => e.stopPropagation()}>
                        {currentView === 'list' && (
                            <TicketListView
                                onSelectTicket={id => { setSelectedTicketId(id); setCurrentView('detail'); }}
                                onNewTicket={() => setCurrentView('new')}
                                onGroupUpdate={setAwaitingCount}
                                onClose={handleCloseModal}
                            />
                        )}
                        {currentView === 'detail' && (
                            <TicketDetailView
                                ticketId={selectedTicketId}
                                onBack={() => { setSelectedTicketId(null); setCurrentView('list'); }}
                                onClose={handleCloseModal}
                            />
                        )}
                        {currentView === 'new' && (
                            <NewTicketForm
                                user={user}
                                onTicketCreated={id => { setSelectedTicketId(id); setCurrentView('detail'); }}
                                onCancel={() => setCurrentView('list')}
                                onClose={handleCloseModal}
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    );
}